import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { http, unwrap } from './client'
import { retryUnlessRefused, workspacePath } from './workspaces'
import { actionKeys } from './intelligence-actions'
import type { ApiEnvelope } from '@/types/api'
import type { MetricTarget, Origin, Person, ReportKind, SharedReport, TaskPriority, TaskStatus } from '@/types/intelligence'
import type { OperationsSnapshot } from './snapshot'
import type { ContinuitySnapshot } from './continuity-snapshot'
import type { OpportunityDetail, OpportunitySummary } from './opportunities'
import type { AnalystAnswer } from './analyst'

/** A follow-up as a report froze it. */
export interface ReportFollowUp {
  id: string
  title: string
  status: TaskStatus
  priority: TaskPriority
  dueDate: string | null
  overdue: boolean
  assignedTo: Person
  completedAt: string | null
  origin: Origin
}

export type ReportObjective = Omit<MetricTarget, 'needsReview' | 'canChange'>

/** Care Quality & Continuity as a dossier froze it (DECISIONS.md #39): the Overview's other perspective, its period the dossier's own. */
export type DossierContinuity = Omit<ContinuitySnapshot, 'period'>

/**
 * A meeting dossier (P6 §4.1): what the Overview, in both its perspectives,
 * Opportunities and Objectives showed for its period. A version 1 dossier
 * was saved before dossiers could include Care Quality & Continuity, and has
 * no `continuity`.
 */
export interface DossierContent {
  version: 1 | 2
  kind: Exclude<ReportKind, 'OPPORTUNITY' | 'ANSWER'>
  period: OperationsSnapshot['period']
  dataHealth: OperationsSnapshot['dataHealth']
  metrics: OperationsSnapshot['metrics']
  whatChanged: OperationsSnapshot['whatChanged']
  /** Absent from a dossier saved before DECISIONS.md #34, which compared every period. */
  recordsFrom?: OperationsSnapshot['recordsFrom']
  comparable?: OperationsSnapshot['comparable']
  opportunities: OpportunitySummary[]
  objectives: ReportObjective[]
  followUps: ReportFollowUp[]
  continuity?: DossierContinuity
}

export interface OpportunityReportContent {
  version: 1
  kind: 'OPPORTUNITY'
  opportunity: OpportunityDetail
  objectives: ReportObjective[]
  followUps: ReportFollowUp[]
}

/** One Elfie answer as its asker saw it (DECISIONS.md #32), without their declared function. */
export interface AnswerReportContent {
  version: 1
  kind: 'ANSWER'
  question: string
  answer: Omit<AnalystAnswer, 'readerFunction'>
}

export interface ReportDetail extends SharedReport {
  content: DossierContent | OpportunityReportContent | AnswerReportContent
}

/** What Generate freezes: a dossier, one opportunity, or one of the reader's own Elfie answers. */
export type GenerateReportRequest =
  | { kind: (typeof DOSSIER_KINDS)[number] }
  | { kind: 'OPPORTUNITY'; opportunityId: string }
  | { kind: 'ANSWER'; answerId: string }

/** The four meeting dossiers, in the order of their horizons (P6 §4.1). */
export const DOSSIER_KINDS = ['TODAY', 'WEEKLY', 'MONTHLY', 'EXECUTIVE'] as const satisfies readonly ReportKind[]

export const reportKeys = {
  all: (workspaceId: string) => [...actionKeys.all(workspaceId), 'reports'] as const,
  one: (workspaceId: string, id: string) => [...reportKeys.all(workspaceId), 'one', id] as const,
}

export const useReports = (workspaceId: MaybeRefOrGetter<string>) =>
  useQuery({
    queryKey: computed(() => reportKeys.all(toValue(workspaceId))),
    queryFn: async () => unwrap((await http.get<ApiEnvelope<SharedReport[]>>(`${workspacePath(toValue(workspaceId))}/reports`)).data),
    retry: retryUnlessRefused,
  })

export const useReport = (workspaceId: MaybeRefOrGetter<string>, id: MaybeRefOrGetter<string>) =>
  useQuery({
    queryKey: computed(() => reportKeys.one(toValue(workspaceId), toValue(id))),
    queryFn: async () =>
      unwrap((await http.get<ApiEnvelope<ReportDetail>>(`${workspacePath(toValue(workspaceId))}/reports/${encodeURIComponent(toValue(id))}`)).data),
    // A saved report never changes.
    staleTime: Infinity,
    retry: retryUnlessRefused,
  })

/** Generate: freezes what Intelligence shows now (P6 §4). */
export const useGenerateReport = (workspaceId: MaybeRefOrGetter<string>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (request: GenerateReportRequest) =>
      unwrap((await http.post<ApiEnvelope<ReportDetail>>(`${workspacePath(toValue(workspaceId))}/reports`, request)).data),
    onSuccess: (report) => {
      queryClient.setQueryData(reportKeys.one(toValue(workspaceId), report.id), report)
      return queryClient.invalidateQueries({ queryKey: reportKeys.all(toValue(workspaceId)) })
    },
  })
}
